import { useId, useState } from 'react';
import { Panel } from '../ui/Panel';
import { btnDestructive, btnGhost, btnSecondary, inputCls } from '../ui/styles';
import { dayMedium } from '../../core/format/dates';
import type { CustomerFileControl } from './useCustomerFile';
type Props = {
  ctl: CustomerFileControl;
};

/**
 * "Niet online laten boeken": de klant kan via de site geen aanvraag meer doen en krijgt dan
 * alleen te zien dat ze de salon belt of appt; de reden blijft intern. Sarah plant haar zelf in
 * via de Agenda of het dossier. Bewaart direct, los van de notitie.
 */
export function BookingBlockPanel({
  ctl
}: Props) {
  const id = useId();
  const block = ctl.file.bookingBlock ?? null;
  const first = ctl.file.account.customer.firstName;
  const [editing, setEditing] = useState(false);
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);
  const busy = ctl.isBusy('booking-block');
  const problem = reason.trim() ? null : 'Schrijf kort op waarom.';
  const turnOn = async () => {
    setTouched(true);
    if (problem) return;
    if (await ctl.saveBookingBlock({
      blocked: true,
      reason: reason.trim()
    })) {
      setEditing(false);
      setReason('');
      setTouched(false);
    }
  };
  return <Panel title="Online boeken" description={block ? undefined : `${first} kan zelf een afspraak aanvragen op de site.`} data-id="element-1088" data-mp="1gy1na9gg08yo">
      {block ? <div className="space-y-3" data-id="element-1089" data-mp="1c0ibrhb4qaaf">
          <p className="text-[14px] text-ink" data-id="element-1090" data-mp="29m7b552sme2q">
            Staat uit sinds {dayMedium(block.since)}
            {block.by ? ` (${block.by})` : ''}.
          </p>
          <p className="rounded-md bg-shell px-3 py-2 text-[13px] text-ink" data-id="element-1091" data-mp="24p752u5y1bbm">“{block.reason}”</p>
          <p className="text-[13px] text-muted" data-id="element-1092" data-mp="1ztrt67rhxphu">Op de site krijgt ze alleen te zien dat ze de salon belt of appt. Jij kunt haar gewoon inplannen.</p>
          <button type="button" onClick={() => void ctl.saveBookingBlock({
        blocked: false,
        reason: ''
      })} disabled={busy} aria-busy={busy} className={btnSecondary} data-id="element-1093" data-mp="2sdgus76a2j81">
            {busy ? 'Bewaren…' : 'Weer online laten boeken'}
          </button>
        </div> : editing ? <div className="space-y-3" data-id="element-1094" data-mp="pq9tsd1sfn1s">
          <label htmlFor={id} className="block text-[13px] text-ink" data-id="element-1095" data-mp="pp8kad0f78m6">
            Waarom? <span className="text-muted" data-id="element-1096" data-mp="bxaikn91gf01">(alleen voor jou)</span>
          </label>
          <input id={id} value={reason} onChange={e => setReason(e.target.value)} maxLength={300} placeholder="Bijv. twee keer niet gekomen zonder afmelden" aria-invalid={touched && Boolean(problem)} aria-describedby={touched && problem ? `${id}-error` : undefined} className={inputCls} data-id="element-1097" data-mp="gwwsampnrd43" />
          {touched && problem && <p id={`${id}-error`} role="alert" className="text-[12px] text-clay" data-id="element-1098" data-mp="3u4i41pe3bihm">
              {problem}
            </p>}
          <div className="flex flex-wrap gap-2" data-id="element-1099" data-mp="1nbo2jh0o8aas">
            <button type="button" onClick={() => void turnOn()} disabled={busy} aria-busy={busy} className={btnDestructive} data-id="element-1100" data-mp="1sbk6xhda63qv">
              {busy ? 'Bewaren…' : 'Online boeken uitzetten'}
            </button>
            <button type="button" onClick={() => setEditing(false)} disabled={busy} className={btnGhost} data-id="element-1101" data-mp="1xiqkvvm6pgpy">
              Annuleren
            </button>
          </div>
        </div> : <button type="button" onClick={() => setEditing(true)} className="text-[13px] text-muted underline underline-offset-2 hover:text-clay" data-id="element-1102" data-mp="26h2kgn513ouj">
          Niet meer online laten boeken…
        </button>}
    </Panel>;
}